import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Copy, RefreshCw, Loader2, Trash2, KeyRound } from "lucide-react";
import { getErrorMessage } from "@/lib/errors";

type StatusResp = {
  seeded: boolean;
  parent?: { id: string; email: string };
  teen?: { id: string; email: string };
  household?: { id: string; name: string } | null;
  child_profile?: { id: string; display_name: string } | null;
  accounts?: Array<{ account_type: string; account_name: string; balance: number; status: string }>;
  subscription?: { status: string; billing_exempt: boolean } | null;
  beta?: Array<{ email: string; status: string }>;
};

const PARENT_EMAIL = "parent.beta@heirwise.app";
const TEEN_EMAIL = "teen.beta@heirwise.app";

async function invoke(action: "status" | "seed" | "reset", body: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("seed-beta-test-household", {
    body: { action, ...body },
  });
  if (error) throw error;
  return data;
}

function copy(text: string, label: string, toast: ReturnType<typeof useToast>["toast"]) {
  navigator.clipboard.writeText(text).then(() => toast({ title: `Copied ${label}` }));
}

export default function AdminQaTestHousehold() {
  const { toast } = useToast();
  const [status, setStatus] = useState<StatusResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [parentPw, setParentPw] = useState("");
  const [teenPw, setTeenPw] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      setStatus(await invoke("status"));
    } catch (e: unknown) {
      toast({ title: "Status failed", description: getErrorMessage(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  useEffect(() => { void refresh(); }, []);

  async function onSeed() {
    if (!status?.seeded && (parentPw.length < 8 || teenPw.length < 8)) {
      toast({ title: "Passwords required", description: "First-time seed needs both passwords (min 8 chars).", variant: "destructive" });
      return;
    }
    setBusy("seed");
    try {
      const res = await invoke("seed", {
        parent_password: parentPw || undefined,
        teen_password: teenPw || undefined,
      });
      toast({ title: "Seed complete", description: `Household ${res.household_id}` });
      setParentPw("");
      setTeenPw("");
      await refresh();
    } catch (e: unknown) {
      toast({ title: "Seed failed", description: getErrorMessage(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  async function onReset() {
    if (!confirm("Reset the Beta Test Household? This wipes seeded progress and transactions but keeps logins.")) return;
    setBusy("reset");
    try {
      await invoke("reset");
      toast({ title: "Reset complete" });
      await refresh();
    } catch (e: unknown) {
      toast({ title: "Reset failed", description: getErrorMessage(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-heading">Beta Test Household</h1>
          <p className="text-sm text-muted-foreground">
            Create and reset a safe, linked parent + teen test family. Admin-only. Passwords are
            sent securely to the backend and never stored in code.
          </p>
        </header>

        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Status</h2>
            <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
          {loading && !status ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !status?.seeded ? (
            <p className="text-sm text-amber-600">Not yet seeded. Set passwords below and run Seed.</p>
          ) : (
            <div className="space-y-3 text-sm">
              <Row label="Parent" value={PARENT_EMAIL} onCopy={() => copy(PARENT_EMAIL, "parent email", toast)} />
              <Row label="Teen" value={TEEN_EMAIL} onCopy={() => copy(TEEN_EMAIL, "teen email", toast)} />
              <Row label="Household" value={status.household?.id ?? "—"} />
              <Row label="Child Profile" value={status.child_profile?.id ?? "—"} />
              <Row label="Subscription" value={`${status.subscription?.status ?? "—"} · exempt=${status.subscription?.billing_exempt}`} />
              <Row label="Beta" value={(status.beta ?? []).map(b => `${b.email}: ${b.status}`).join(" · ")} />
              <div>
                <div className="font-medium mb-1">Sim Wallet</div>
                <ul className="text-xs space-y-1">
                  {(status.accounts ?? []).map(a => (
                    <li key={a.account_type} className="flex justify-between border-b border-border/40 py-1">
                      <span>{a.account_name} <span className="text-muted-foreground">({a.account_type})</span></span>
                      <span className="tabular-nums">${(a.balance / 100).toFixed(2)} · {a.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2 pt-2 text-xs">
                <Button size="sm" variant="outline" onClick={() => copy("/parent-dashboard", "parent route", toast)}>
                  /parent-dashboard
                </Button>
                <Button size="sm" variant="outline" onClick={() => copy("/dashboard", "teen route", toast)}>
                  /dashboard
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            <h2 className="font-semibold">{status?.seeded ? "Reset passwords (optional)" : "Set initial passwords"}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Parent password</Label>
              <Input
                type="password"
                value={parentPw}
                onChange={(e) => setParentPw(e.target.value)}
                placeholder="min 8 chars"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label>Teen password</Label>
              <Input
                type="password"
                value={teenPw}
                onChange={(e) => setTeenPw(e.target.value)}
                placeholder="min 8 chars"
                autoComplete="new-password"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Leave blank when re-running seed to keep existing passwords. Or use the normal /forgot-password flow.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={onSeed} disabled={busy !== null}>
              {busy === "seed" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {status?.seeded ? "Re-seed / Repair" : "Seed Beta Test Household"}
            </Button>
            <Button variant="destructive" onClick={onReset} disabled={busy !== null || !status?.seeded}>
              {busy === "reset" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Reset progress & wallet
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs break-all">{value}</span>
        {onCopy ? (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCopy}>
            <Copy className="w-3.5 h-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}